import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api, money } from './costingApi';
import { NewAcquisitionDialog } from './NewAcquisitionDialog';
import { AcquisitionDetail } from './AcquisitionDetail';
import { ProfitabilityTable } from './ProfitabilityTable';
import { CogsReview, LedgerView } from './CogsLedger';
import { FundingPanel } from './FundingPanel';
import { FinancialDashboard } from './FinancialDashboard';
import { MarginAlerts } from './MarginAlerts';

const AcquisitionsList = ({ rows, onOpen }) => (
  <div className="overflow-x-auto border border-slate-700 rounded">
    <table className="w-full text-sm">
      <thead className="bg-slate-800 text-slate-300 text-xs"><tr>{['ID', 'Date', 'Vehicle / batch', 'Supplier', 'Total cost', 'Funding', 'Allocated', 'Unallocated', 'Status'].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
      <tbody>{rows.map((a) => (
        <tr key={a.id} onClick={() => onOpen(a.id)} className="border-t border-slate-700 hover:bg-slate-800 cursor-pointer" data-testid={`acq-row-${a.id}`}>
          <td className="p-2 font-mono text-xs">{a.acq_no}</td><td className="p-2">{a.purchase_date}</td>
          <td className="p-2">{a.kind === 'batch' ? 'Parts batch' : `${a.make} ${a.model}`} <span className="capitalize text-slate-400">{a.engine_type}</span> <span className="font-mono text-slate-400">{a.registration}</span></td>
          <td className="p-2">{a.supplier}</td><td className="p-2">{money(a.total_cost)}</td>
          <td className="p-2 capitalize">{(a.funding_source || '').replace('_', ' ')} · {a.payment_status}</td>
          <td className="p-2">{money(a.allocation.allocated)}</td>
          <td className={`p-2 ${a.allocation.unallocated ? 'text-rose-400' : 'text-emerald-400'}`}>{money(a.allocation.unallocated)}</td>
          <td className="p-2"><Badge className={a.allocation_status === 'finalized' ? 'bg-emerald-700' : 'bg-amber-600'}>{a.allocation_status}</Badge></td>
        </tr>
      ))}
      {rows.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-slate-400">No acquisitions recorded yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function VehiclesCostingPanel() {
  const [acqs, setAcqs] = useState([]);
  const [report, setReport] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    api.get('/acquisitions').then(setAcqs).catch(() => {});
    api.get('/acquisitions/reports/profitability').then(setReport).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4 text-white" data-testid="vehicles-costing-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold">Auto Spares Finance — Stock, COGS, Vehicles &amp; Funding</h2>
          <p className="text-xs text-slate-400">Stripping vehicles and batches are held as inventory; COGS posts automatically on every sale.</p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-acquisition-btn"><Plus className="h-4 w-4 mr-1" />New acquisition</Button>
      </div>
      <Tabs defaultValue="dashboard">
        <TabsList className="bg-slate-800 flex-wrap h-auto">
          <TabsTrigger value="dashboard" data-testid="costing-tab-dashboard">Financial Dashboard</TabsTrigger>
          <TabsTrigger value="profitability" data-testid="costing-tab-profitability">Vehicle Profitability</TabsTrigger>
          <TabsTrigger value="acquisitions" data-testid="costing-tab-acquisitions">Acquisitions &amp; Allocation</TabsTrigger>
          <TabsTrigger value="cogs-review" data-testid="costing-tab-cogs-review">COGS Review</TabsTrigger>
          <TabsTrigger value="margin-alerts" data-testid="costing-tab-margin-alerts">Margin Alerts</TabsTrigger>
          <TabsTrigger value="funding" data-testid="costing-tab-funding">JSM Trading Funding</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="costing-tab-ledger">Ledger</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><FinancialDashboard /></TabsContent>
        <TabsContent value="funding"><FundingPanel /></TabsContent>
        <TabsContent value="profitability"><ProfitabilityTable report={report} onOpen={setOpenId} /></TabsContent>
        <TabsContent value="acquisitions"><AcquisitionsList rows={acqs} onOpen={setOpenId} /></TabsContent>
        <TabsContent value="cogs-review"><CogsReview /></TabsContent>
        <TabsContent value="margin-alerts"><MarginAlerts /></TabsContent>
        <TabsContent value="ledger"><LedgerView /></TabsContent>
      </Tabs>
      <NewAcquisitionDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(a) => { load(); setOpenId(a.id); }} />
      <AcquisitionDetail acqId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
